/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  CLIENT RBAC IS UX ONLY. THE SERVER IS THE BOUNDARY.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Everything in this module exists so an operator is not shown a door they cannot open. It is
 * NOT security. Enforcement is the API's `require_scope(...)` dependency on every admin router,
 * always, without exception.
 *
 * Three things that follow from that, and are easy to get wrong:
 *   - Never reason "the UI hides it, so the endpoint is safe."
 *   - Never gate a *mutation* on a client check alone.
 *   - Never add a client-side permission the backend does not also enforce. A scope check that
 *     exists here and nowhere else is a vulnerability wearing a feature's clothes.
 *
 * The scope set is fixed by R9 and is **role-shaped, not resource-shaped**. An earlier
 * eighteen-scope draft is superseded; do not resurrect it. Colon-style strings that appear in
 * some backend feature docs (`pricing:publish`, `finance:release`) are markers of a matrix cell,
 * not wire values — on the wire there are exactly these seven strings.
 */

export const SCOPES = [
  'superadmin',
  'ops_dispatch',
  'supply_kyc',
  'finance',
  'catalogue_content',
  'support',
  'growth',
] as const;

export type Scope = (typeof SCOPES)[number];

/** What each scope owns, in the words the roster screen shows. */
export const SCOPE_OWNS: Record<Scope, string> = {
  superadmin:
    'Everything, including admin and scope management, safety-blast-radius flags, and destructive ops needing co-sign',
  ops_dispatch: 'The dispatch board: bookings, assignment, replacement, day-of exceptions',
  supply_kyc: 'Pandit onboarding, KYC review, badges, provider suspension',
  finance: 'Money: payments, ledger, payouts, refunds, tax reports',
  catalogue_content: 'Pujas, packages, pricing, temples, cities, CMS/SEO pages, media library',
  support: 'Tickets, disputes intake, devotee account service, per-booking context reads',
  growth: 'Offers, referrals, campaigns and notifications',
};

export function isScope(value: string): value is Scope {
  return (SCOPES as readonly string[]).includes(value);
}

/**
 * Can this scope reach something that requires `need`?
 *
 * `superadmin` passes everything — it is the founder/owner role and holds every matrix cell.
 * A `need` of several scopes means any one of them suffices.
 */
export function can(held: Scope, need: Scope | readonly Scope[]): boolean {
  if (held === 'superadmin') return true;
  return Array.isArray(need) ? (need as readonly Scope[]).includes(held) : held === need;
}

/** True when the operator holds at least one of these. Used for nav-group visibility. */
export function canAny(held: Scope, needs: readonly Scope[]): boolean {
  return needs.some((n) => can(held, n));
}

/** Human phrasing for a denial tooltip — "Requires scope: finance or support". */
export function describeNeed(need: Scope | readonly Scope[]): string {
  return Array.isArray(need) ? (need as readonly Scope[]).join(' or ') : String(need);
}
